import { useEffect, useState } from 'react';
import { Settings2, Server, User, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/store/auth';
import { primeAudio } from '@/lib/ringtone';
import { getServerUrl, setServerUrl } from '@/lib/api';
import { isDesktop, setWindowSize } from '@/lib/desktop';

const DEFAULT_PLACEHOLDER = 'https://ccark.arkenom.com.tr';

export default function Login() {
  const { login, loading } = useAuth();
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [showServer, setShowServer] = useState(isDesktop || !!getServerUrl());
  const [server, setServer] = useState(getServerUrl());

  // Tauri: login ekranında pencereyi kompakt tut (geri logout durumunda da)
  useEffect(() => { void setWindowSize(540, 720); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    primeAudio();
    if (showServer) setServerUrl(server.trim() || null);
    try {
      await login(u, p);
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message || e?.response?.data?.message || 'Giriş başarısız');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/40 p-5 relative overflow-hidden">
      {/* Animated background orbs — brand gradient */}
      <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-gradient-to-br from-brand-400/30 to-brand-600/20 blur-3xl pointer-events-none animate-[pulse_6s_ease-in-out_infinite]" />
      <div className="absolute -bottom-48 -left-48 w-[32rem] h-[32rem] rounded-full bg-gradient-to-tr from-amber-400/25 to-brand-500/15 blur-3xl pointer-events-none animate-[pulse_8s_ease-in-out_infinite] [animation-delay:2s]" />
      <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full bg-gradient-to-br from-rose-300/15 to-brand-400/10 blur-3xl pointer-events-none animate-[pulse_10s_ease-in-out_infinite] [animation-delay:4s]" />

      {/* Subtle dot grid background */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #0f172a 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative w-full max-w-sm">
        {/* Brand mark — text-based (ArkBase tarzı) */}
        <div className="text-center mb-8">
          <h1 className="text-[44px] font-bold tracking-tight leading-none">
            <span className="text-slate-900">Ark</span><span className="text-brand-500 font-semibold">Connect</span>
            <span className="text-brand-500">.</span>
          </h1>
          <p className="text-[14px] text-slate-500 mt-3">Çağrı merkezi temsilci paneli</p>
        </div>

        {/* Form card */}
        <form
          onSubmit={submit}
          className="bg-surface/70 backdrop-blur-2xl rounded-3xl p-6 space-y-3 ring-1 ring-black/5 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.15),0_4px_16px_-4px_rgba(15,23,42,0.06)]"
        >
          <IosField icon={<User size={16} />}>
            <input
              autoFocus
              value={u}
              onChange={(e) => setU(e.target.value)}
              placeholder="Kullanıcı adı"
              className="w-full bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </IosField>

          <IosField icon={<Lock size={16} />}>
            <input
              type="password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              placeholder="Şifre"
              className="w-full bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </IosField>

          {showServer && (
            <IosField icon={<Server size={16} />}>
              <input
                type="url"
                value={server}
                onChange={(e) => setServer(e.target.value)}
                placeholder={DEFAULT_PLACEHOLDER}
                className="w-full bg-transparent text-[14px] font-mono text-slate-700 placeholder:text-slate-400 focus:outline-none"
                aria-label="Sunucu adresi"
              />
            </IosField>
          )}

          {err && (
            <div className="px-3.5 py-2.5 bg-red-50 rounded-xl text-[13px] text-red-700 ring-1 ring-red-100 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-red-500 mt-2 flex-shrink-0" />
              {err}
            </div>
          )}

          <button
            disabled={loading || !u || !p}
            className="group relative w-full mt-2 flex items-center justify-center gap-1.5 py-3 bg-gradient-to-br from-brand-500 to-brand-600 hover:from-brand-400 hover:to-brand-700 active:from-brand-600 active:to-brand-700 disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-400 text-[#08140d] text-[15px] font-semibold rounded-2xl transition-all active:scale-[0.99] shadow-[0_8px_20px_-6px_rgba(255,79,31,0.55)] hover:shadow-[0_12px_28px_-8px_rgba(255,79,31,0.65)] disabled:shadow-none overflow-hidden"
          >
            {/* subtle shine */}
            <span className="absolute inset-0 bg-gradient-to-t from-white/0 via-white/0 to-white/15 pointer-events-none" />
            <span className="relative flex items-center gap-1.5">
              {loading ? 'Giriliyor…' : (
                <>
                  Devam Et
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </span>
          </button>

          {!showServer && (
            <button
              type="button"
              onClick={() => setShowServer(true)}
              className="w-full flex items-center justify-center gap-1.5 text-[12px] text-slate-500 hover:text-brand-600 transition pt-1"
            >
              <Settings2 size={11} />
              Sunucu adresini değiştir
            </button>
          )}
        </form>

        <p className="text-center text-[11px] text-slate-400 mt-5">
          <span className="font-semibold text-slate-500">Ark</span><span className="text-slate-400">Connect</span>
          {' · '}Arkenom Çağrı Merkezi Platformu
        </p>
      </div>
    </div>
  );
}

function IosField({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-2.5 px-3.5 py-2.5 bg-slate-100/70 rounded-xl ring-1 ring-transparent focus-within:bg-surface focus-within:ring-brand-300 focus-within:shadow-[0_0_0_3px_rgba(255,79,31,0.12)] transition-all">
      <span className="text-slate-400 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </label>
  );
}
